import { Stack, Typography, Button, Collapse, buttonClasses, TextField, useTheme, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState, useRef, useEffect } from "react";
import { DIALOG_VARIANTS } from "@hyro-components/Dialogs/DialogConsts";
import Dialog from "@hyro-components/Dialogs/HyroUiDialog";
import useCallMeDialog from "./CallMeDialog.hook";
import { CALL_ME_FIELDS } from "@phrasing";
import HyroSingleSelect from "src/components/hyro-components/fields/single-option/HyroSingleSelect";
import ChevronDown from "src/icons/ChevronDown";
import Field from "@hyro-components/fields/HyroField";
import { FIELD_TYPES } from "@hyro-components/fields/utils";

import { isOutboundAgentSelector, hasPermissionSelector } from "src/stores/selectors/appSelectors";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useAppStore from "src/stores/appStore";
import useExtendPreview from "../../preview/extend-preview/ExtendPreview.hook";
import ExtendPreviewBanner from "../../preview/extend-preview/ExtendPreviewBanner";

const CallMeDialog = ({ isOpen, onClose, callRequestSent, handleCallMe, hideBackdrop, PaperProps, variant = DIALOG_VARIANTS.transactional, addSnackbarMessage, setSnackbarSeverity }) => {
  const { sites, businessHoursModes, errors, fields, disableCall, loading, handleChange, fieldsForServer, systemMode } = useCallMeDialog();
  const { isExtendingPreview, handleExtendPreview, getExpirationText } = useExtendPreview(addSnackbarMessage, setSnackbarSeverity);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isOutboundAgent = useAppStore(isOutboundAgentSelector);
  const hasPermission = useAppStore(hasPermissionSelector);
  const endOfAdvancedRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    if (showAdvanced && endOfAdvancedRef.current) {
      setTimeout(() => {
        endOfAdvancedRef.current.scrollIntoView({ behavior: "smooth" });
      }, 300); // Small delay to allow Collapse animation to start
    }
  }, [showAdvanced]);

  return (
    <Dialog
      showCloseButton={variant === DIALOG_VARIANTS.transactional}
      testId="call-me-dialog"
      title="Test your Hyro AI Agent"
      open={isOpen}
      hideBackdrop={hideBackdrop}
      variant={variant}
      handleSubmit={() => handleCallMe(fieldsForServer)}
      handleCancel={onClose}
      handleClose={onClose}
      submitButtonLoading={callRequestSent}
      disableSave={!!disableCall}
      submitButtonText="Call me"
      PaperProps={PaperProps}
    >
      <DialogContent>
        {
          hasPermission(USER_PERMISSIONS.SHARE_PREVIEW.READ)
            ? <Box sx={{ mt: 1 }}>
              <ExtendPreviewBanner getExpirationText={getExpirationText} handleExtendPreview={handleExtendPreview} isExtendingPreview={isExtendingPreview} />
            </Box>
            : null
        }

        <InstructionsBox>
          <InstructionsText>
            <span style={{ color: theme.palette.text[700] }}>
              {CALL_ME_FIELDS.DIALOG_DESCRIPTION[systemMode]}
            </span>
            <LearnMoreLink href="https://docs.hyro.ai/docs/agent-lifecycle" target="_blank" rel="noopener noreferrer">
              Learn more
            </LearnMoreLink>
          </InstructionsText>
        </InstructionsBox>

        <Stack>
          <InputField
            errorColor={theme.palette.error[500]}
            testId="phone-number-field"
            disabled={callRequestSent}
            field={CALL_ME_FIELDS.PHONE_NUMBER} placeholder={CALL_ME_FIELDS.PHONE_NUMBER.PLACEHOLDER}
            errors={errors} value={fields[CALL_ME_FIELDS.PHONE_NUMBER.ID]} mandatory onChange={handleChange}
            onEnterPress={() => handleCallMe(fieldsForServer)}
          />

          {isOutboundAgent ? <Stack>
            <Typography variant="subtitle2" color="text.800">{CALL_ME_FIELDS.OUTBOUND_JSON.LABEL} <span style={{ color: theme.palette.error[500] }}> *</span></Typography>
            <Typography sx={{ mb: 2 }} variant="body2"
              color="text.800">{CALL_ME_FIELDS.OUTBOUND_JSON.INSTRUCTIONS}</Typography>
            <TextField
              multiline
              rows={3}
              error={!!errors[CALL_ME_FIELDS.OUTBOUND_JSON.ID]}
              helperText={errors[CALL_ME_FIELDS.OUTBOUND_JSON.ID]}
              value={fields[CALL_ME_FIELDS.OUTBOUND_JSON.ID]}
              onChange={(e) => handleChange({ target: { name: CALL_ME_FIELDS.OUTBOUND_JSON.ID, value: e.target.value } })}
              placeholder={CALL_ME_FIELDS.OUTBOUND_JSON.PLACEHOLDER}
            />
          </Stack> : null}

          <StyledAdvancedButton
            data-testid="advanced-options-button"
            variant="clear"
            endIcon={<ChevronDown stroke="#4548C4" style={{
              width: "18px",
              transform: showAdvanced ? "rotate(180deg)" : ""
            }} />}
            onClick={() => setShowAdvanced(prev => !prev)}
          >
            Advanced Options
          </StyledAdvancedButton>

          <Collapse in={showAdvanced} sx={{ mt: showAdvanced ? 2 : 0 }}>
            <Stack gap={3}>
              <InputField
                errorColor={theme.palette.error[500]}
                testId="caller-id-field"
                disabled={callRequestSent}
                field={CALL_ME_FIELDS.CALLER_ID} placeholder={CALL_ME_FIELDS.CALLER_ID.PLACEHOLDER} errors={errors}
                value={fields[CALL_ME_FIELDS.CALLER_ID.ID]} onChange={handleChange}
                onEnterPress={() => handleCallMe(fieldsForServer)}
              />

              {!isOutboundAgent ? <InputField
                errorColor={theme.palette.error[500]}
                testId="sites-field"
                disabled={callRequestSent}
                type='select'
                field={CALL_ME_FIELDS.SITES}
                placeholder={CALL_ME_FIELDS.SITES.PLACEHOLDER}
                errors={errors}
                options={sites}
                value={fields[CALL_ME_FIELDS.SITES.ID]}
                onChange={handleChange}
                loading={loading}
              /> : null}

              {!isOutboundAgent ? <InputField
                errorColor={theme.palette.error[500]}
                testId="business-hours-field"
                disabled={callRequestSent}
                type='select'
                field={CALL_ME_FIELDS.OPERATING_HOURS}
                placeholder={CALL_ME_FIELDS.OPERATING_HOURS.PLACEHOLDER}
                errors={errors}
                options={businessHoursModes}
                value={fields[CALL_ME_FIELDS.OPERATING_HOURS.ID]}
                onChange={handleChange}
                loading={loading}
              /> : null}

              <div ref={endOfAdvancedRef} data-testid="end-of-advanced-section" />
            </Stack>
          </Collapse>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const InputField = ({ field, type = "text", value, placeholder, errors, onChange, options, loading, disabled, mandatory = false, testId, onEnterPress, errorColor }) => <Stack>
  <Stack sx={{ mb: 2 }}>
    <Typography variant="subtitle2" color="text.800">
      {field.LABEL} {mandatory && <span style={{ color: "#EF404B" }}> *</span>}
    </Typography>

    {field.INSTRUCTIONS && <Typography variant="body2" color="text.800">
      {field.INSTRUCTIONS}
    </Typography>}
  </Stack>

  {type === "select" ?
    <HyroSingleSelect
      testId={testId}
      disabled={disabled}
      placeholder={placeholder}
      loading={loading}
      error={!!errors[field.ID]}
      onChange={(value) => onChange({ target: { name: field.ID, value } })}
      label={field.LABEL}
      options={options}
      value={value} /> :
    <Field
      fieldType={FIELD_TYPES.PHONE}
      testId={testId}
      disabled={disabled}
      name={field.ID}
      label={""}
      placeholder={placeholder}
      error={errors[field.ID]}
      value={value}
      onChange={val => onChange({ target: { name: field.ID, value: val } })}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !Object.values(errors).some(Boolean) && onEnterPress) {
          event.preventDefault();
          onEnterPress();
        }
      }}
    />}

</Stack >;

const DialogContent = styled("div")(() => ({
  display: "flex",
  flexFlow: "column"
}));

const InstructionsText = styled("div")(({ theme }) => ({
  fontSize: "16px",
  lineHeight: "24px",
  letterSpacing: "0.15px",
  color: theme.palette.text[800]
}));

const LearnMoreLink = styled("a")(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: "none",
  fontSize: "16px",
  lineHeight: "24px",
  letterSpacing: "0.15px",
  "&:hover": {
    textDecoration: "underline"
  }
}));

const InstructionsBox = styled("div")(() => ({
  marginBottom: "24px"
}));

const StyledAdvancedButton = styled(Button)(({ theme }) => ({
  alignSelf: "flex-start",
  padding: "12px",
  paddingBottom: "4px",
  color: theme.palette.primary.main,
  fontSize: "14px",
  [`& .${buttonClasses.endIcon}`]: {
    marginLeft: "4px"
  }
}));

export default CallMeDialog;
